import type { MutableRefObject, ReactElement, ReactNode } from 'react'
import React, { useMemo, useReducer } from 'react'
import BaseTable from './BaseTable'
import BulkActions from './BulkActions'
import GlobalActions from './GlobalActions'
import Header from './Header'
import PaginationComponent from './Pagination'
import Search from './Search'
import type {
  ColumnWidth,
  Limit,
  Pagination,
  SelectEventHandler,
  TableColumn,
  TableRow,
  TableState,
} from './TableContext'
import { runReducer, TableContext } from './TableContext'
import Title from './Title'
import Toolbar from './Toolbar'

export interface BasicTableColumn {
  /**
   * Unique identifier for the column. Used to match column definitions with row data
   */
  key: string
  /**
   * The content to display in the column header
   */
  value: ReactNode
  /**
   * The width configuration for the column supporting a percentage value.
   * If not provided, the column will size automatically based on its content.
   */
  width?: ColumnWidth
}

export interface BasicTableProps {
  /**
   * Child components to render within the table's header.
   * Can include `Table.Header` and `Table.Toolbar` components.
   */
  children?: ReactElement | ReactElement[]
  /**
   * Array of column definitions that specify the table structure, including column keys, labels, and widths
   */
  columns: BasicTableColumn[]
  /**
   * Displays table in a denser (compact) layout with reduced padding
   * @default false
   */
  dense?: boolean
  /**
   * Maximum number of rows to display per page
   * @default 50
   */
  limit?: Limit
  /**
   * Whether the table is in a loading state
   * @default false
   */
  loading?: boolean
  /**
   * Callback function invoked when rows are selected or deselected
   */
  onSelect?: SelectEventHandler
  /**
   * Type of pagination to display for the table.
   * `simple`: Shows basic previous/next navigation without total page count: e.g. 25-50 of 51+. Note, when using 'simple' pagination, you need to provide one extra row to indicate if there is a next page.
   * `lengthAware': Shows full pagination with total count: e.g. 25-50 of 123
   * @default 'lengthAware'
   */
  pagination?: Pagination
  /**
   * Array of row data objects. Each row must have an "id" property and properties matching column keys
   */
  rows: TableRow[]
  /**
   * Enables vertical scrolling for the table body when content exceeds the viewport height.
   * When `true`, the table will only expand until the end of the viewport. If the table starts below the bottom of the viewport, the table will behave as if `scrollable` is set to false. Use this behaviour when the table is the main content on the page.
   * When `false`, the table will expand to fit its content. Use this behaviour when the table is part of a larger layout or there are multiple tables on the page.
   * @default true
   */
  scrollable?: boolean
  /**
   * Enables row selection with checkboxes. When true, adds a checkbox column and enables bulk selection
   * @default false
   */
  selectable?: boolean
  /**
   * Mutable ref object to track selected row IDs. Used for controlled selection state
   */
  selectionsRef?: MutableRefObject<string[]>
  /**
   * Total number of rows available (for pagination). Defaults to rows.length if not provided
   * @default rows.length
   */
  total?: number
}

function BasicTable({
  children,
  columns: initialColumns,
  dense = false,
  limit = 50,
  loading = false,
  onSelect,
  pagination = 'lengthAware',
  rows,
  scrollable = true,
  selectable = false,
  selectionsRef,
  total,
}: BasicTableProps): JSX.Element {
  const adjustedColumns = useMemo(
    () =>
      initialColumns.map((column) => ({
        ...column,
        selected: true,
        sort: undefined,
      })) as TableColumn[],
    [initialColumns],
  )

  const [state, dispatch] = useReducer(
    runReducer,
    {},
    () =>
      ({
        columns: {
          selected: adjustedColumns,
          unselected: [],
        },
        dense,
        limit,
        loading,
        onSelect,
        page: 1,
        pagination,
        rowActions: undefined,
        rows,
        isScrolling: false,
        scrollable,
        selectable,
        allSelected: false,
        selectionsRef,
        staticColumns: true,
        total: pagination === 'lengthAware' ? total || rows.length : undefined,
      } as TableState),
  )

  return (
    <TableContext.Provider value={[state, dispatch]}>
      <BaseTable
        columns={adjustedColumns}
        dense={dense}
        loading={loading}
        rows={rows}
        scrollable={scrollable}
        selectable={selectable}
        total={total}
      >
        {children}
      </BaseTable>
    </TableContext.Provider>
  )
}

BasicTable.BulkActions = BulkActions
BasicTable.GlobalActions = GlobalActions
BasicTable.Header = Header
BasicTable.Pagination = PaginationComponent
BasicTable.Search = Search
BasicTable.Title = Title
BasicTable.Toolbar = Toolbar

export default BasicTable
